/*
 * The fencer/tournament transpose, shared by Upcoming and Past.
 *
 * WHY THIS IS ONE FUNCTION AND NOT TWO SCREENS' WORTH OF LOOPS. Both §10 and §11
 * describe the same move in the same words — "same components inverted" — over
 * payloads with the same shape: tournaments → events → fencers. The axis choice
 * decides which level is the band and which becomes the rows:
 *
 *   TOURNAMENT AXIS (the default, and the payload's own shape)
 *     band       tournament
 *     sub-header event
 *     card rows  the fencers registered for / placed in that event
 *
 *   FENCER AXIS
 *     band       fencer
 *     sub-header tournament
 *     card rows  that fencer's events within it
 *
 * Written once because two implementations would drift, and the one that drifted
 * would be whichever axis is used less — so the bug would live where nobody
 * looks. It is also the piece with the real subtlety (see the ordering note
 * below), which is worth having in a single tested place rather than twice in
 * JSX.
 *
 * Generic over the three levels rather than typed to one view: Upcoming's fencer
 * row carries a registration status while Past's carries a placement, and neither
 * view should have to widen the other's type to share the traversal.
 */

import { list } from "./api";

/** The minimum each level must carry for the transpose to work. */
export interface HasFencers<F> {
  fencers: F[] | null;
}

export interface HasEvents<E> {
  events: E[] | null;
}

/**
 * Whatever a row carries that identifies a PERSON, for banding and counting.
 *
 * Every field is optional because the payloads carry different halves, and
 * `personKey` below resolves them in order. `fencer_id` in particular is optional
 * because a result is anchored on an alias and identity resolution attaches the
 * person afterwards (decision 345) — so a placement written the moment somebody
 * finished has no fencer id for a while, and Past has to render it regardless.
 */
export interface PersonRef {
  fencer_id?: string | undefined;
  alias_id?: string | undefined;
  name?: string | undefined;
}

/** A PersonRef that can also be LABELLED, which the fencer axis needs and a count does not. */
export interface FencerIdentity extends PersonRef {
  name: string;
  /**
   * A TEAM's row names its people here (decision 714): one row for the whole
   * team, so the fencer axis files it under EACH tracked member rather than
   * under the team (Kevin, 2026-10-12). Absent on a person's row.
   */
  team_members?: { name: string; fencer_id?: string | undefined; tracked: boolean }[] | null | undefined;
}

/**
 * The people a row is about, for banding and counting: the row's own person,
 * or for a team's row each tracked member (decision 714).
 */
export function peopleOf<F extends FencerIdentity>(entry: F): FencerIdentity[] {
  if (entry.team_members && entry.team_members.length > 0) {
    const tracked = entry.team_members
      .filter((m) => m.tracked)
      .map((m) => ({ fencer_id: m.fencer_id, name: m.name }));
    if (tracked.length > 0) return tracked;
  }
  return [entry];
}

/**
 * The identity to band by: the resolved person where we have one, the alias
 * otherwise.
 *
 * Mirrors `mergeKey` in internal/api/views.go, prefix and all, so the two sides
 * of the wire agree on what counts as "the same person". The prefixes matter:
 * without them a fencer id and an alias id are both bare uuids, and one day two
 * of them would collide in a way nobody could reproduce.
 *
 * BOTH FIELDS ARE OPTIONAL because the two payloads that use this carry
 * different halves: Upcoming's registrations always resolve to a fencer and
 * carry no alias, while Past carries the alias always and the fencer only once
 * resolution has run. Every row has at least one — the name is the last resort
 * rather than a real key, and it is here so a payload that somehow has neither
 * still bands by something a reader can recognise instead of collapsing every
 * such row into one band.
 */
export function personKey(f: PersonRef): string {
  if (f.fencer_id) return `f:${f.fencer_id}`;
  if (f.alias_id) return `a:${f.alias_id}`;
  return `n:${f.name ?? ""}`;
}

/** One event within one tournament, for one fencer. */
export interface FencerEvent<E, F> {
  event: E;
  entry: F;
}

/** One tournament within a fencer's band. */
export interface FencerTournament<T, E, F> {
  tournament: T;
  events: FencerEvent<E, F>[];
}

/** One band on the fencer axis. */
export interface FencerGroup<T, E, F> {
  /**
   * The band's identity, from `personKey` — NOT a fencer id, which is why it is
   * not called one. It is prefixed and may name an alias, because an unresolved
   * result has no fencer to name (decision 345). Safe as a React key and as a
   * map key; never pass it to an API expecting a fencer id.
   */
  key: string;
  /**
   * The name to print on the band.
   *
   * Taken from the FIRST appearance rather than recomputed, and that is
   * deliberate: sources rewrite names (invariant 3 — AskFred retroactively
   * renames fencers), so two rows for one fencer can carry different spellings.
   * Picking one and using it consistently is better than a band whose name
   * disagrees with the rows beneath it.
   */
  name: string;
  tournaments: FencerTournament<T, E, F>[];
}

/**
 * Transposes a tournament-shaped payload onto the fencer axis.
 *
 * ORDERING IS THE SUBTLE PART, and it is why this returns arrays rather than
 * Maps. The server sends tournaments in a meaningful order — chronological for
 * Upcoming, reverse-chronological for Past — and that order must survive the
 * transpose, because a fencer's band should still read soonest-first (or
 * most-recent-first) inside itself. Iterating a Map's insertion order gives that
 * for free as long as nothing re-sorts, so nothing here re-sorts.
 *
 * FENCER BANDS ARE ORDERED ALPHABETICALLY, and this is a REVERSAL (decision 326).
 * They were ordered by first appearance, inheriting the payload's ordering so the
 * fencer competing soonest led, on the argument that alphabetical "puts a name
 * with nothing imminent at the top of a screen whose whole axis is time".
 *
 * That argument was written for a list of unbounded length, where leading with
 * what is imminent is the only way to make the top of the screen useful. A list
 * is capped at ten. At ten, every band is on screen or one scroll away, so
 * nothing is buried by ordering — and first-appearance order RESHUFFLES as
 * tournaments come and go, so the one thing it costs is the ability to learn
 * where a name lives. Alphabetical is the same every time you look, which is
 * worth more than putting the soonest name first on a screen you can see all of.
 *
 * The names are stored surname-first ("Dobrosavljević, Kalina"), so ordering by
 * the name string IS ordering by last name. `localeCompare` rather than `<`,
 * because the corpus is full of diacritics and codepoint order puts every accented
 * surname after every unaccented one — "Zajac" before "Ábel".
 */
export function byFencer<T extends HasEvents<E>, E extends HasFencers<F>, F extends FencerIdentity>(
  tournaments: T[] | null | undefined,
): FencerGroup<T, E, F>[] {
  const groups = new Map<string, FencerGroup<T, E, F>>();

  for (const tournament of list(tournaments)) {
    for (const event of list(tournament.events)) {
      for (const entry of list(event.fencers)) {
        // A team's row goes in every tracked member's band (decision 714).
        for (const person of peopleOf(entry)) {
          const key = personKey(person);
          let group = groups.get(key);
          if (!group) {
            group = { key, name: person.name, tournaments: [] };
            groups.set(key, group);
          }

          // The tournament bucket within this fencer's band. Looked up from the
          // END rather than searched, because the payload groups a tournament's
          // events together — so the bucket wanted is almost always the last one
          // added, and a linear search would be doing work to find what is
          // already in hand.
          let bucket = group.tournaments[group.tournaments.length - 1];
          if (!bucket || bucket.tournament !== tournament) {
            bucket = { tournament, events: [] };
            group.tournaments.push(bucket);
          }

          bucket.events.push({ event, entry });
        }
      }
    }
  }

  // Sorted on the way out, so the ordering above — which is the payload's, and
  // which the tournament buckets INSIDE each band still rely on — is untouched.
  // Only the bands are resequenced; a fencer's own tournaments stay in the
  // server's chronological order.
  return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The tournament axis: the payload's own shape, with the empty levels dropped.
 *
 * NOT A NO-OP, which is the reason it exists rather than the screens using the
 * payload directly. An event with no fencer rows is chrome around an absence, and
 * a tournament with no such events is a band with nothing under it. Both must go —
 * and Upcoming can genuinely produce them, because it filters withdrawn
 * registrations client-side (§5 renders a withdrawal honestly rather than dropping
 * it, so the server sends them and the view decides).
 *
 * Doing it here means both axes agree about what "empty" means, which they would
 * not if one screen filtered and the other did not.
 */
export interface TournamentGroup<T, E, F> {
  tournament: T;
  events: { event: E; fencers: F[] }[];
}

export function byTournament<
  T extends HasEvents<E>,
  E extends HasFencers<F>,
  F,
>(
  tournaments: T[] | null | undefined,
  keep: (fencer: F) => boolean = () => true,
): TournamentGroup<T, E, F>[] {
  return list(tournaments)
    .map((tournament) => ({
      tournament,
      events: list(tournament.events)
        .map((event) => ({ event, fencers: list(event.fencers).filter(keep) }))
        .filter((e) => e.fencers.length > 0),
    }))
    .filter((t) => t.events.length > 0);
}

/**
 * Both axes from one call, so a screen cannot compute one and forget the other.
 *
 * The `keep` predicate is applied on BOTH paths, which is the point of routing
 * them through here: a fencer filtered out of the tournament axis but present on
 * the fencer axis would be a difference the toggle exposes, and the toggle is
 * meant to be a regrouping of the same data rather than a different query.
 */
export function regroup<
  T extends HasEvents<E>,
  E extends HasFencers<F>,
  F extends FencerIdentity,
>(
  tournaments: T[] | null | undefined,
  keep: (fencer: F) => boolean = () => true,
): { tournament: TournamentGroup<T, E, F>[]; fencer: FencerGroup<T, E, F>[] } {
  const filtered = list(tournaments).map(
    (t) =>
      ({
        ...t,
        events: list(t.events).map(
          (e) => ({ ...e, fencers: list(e.fencers).filter(keep) }) as E,
        ),
      }) as T,
  );

  return {
    tournament: byTournament<T, E, F>(filtered),
    fencer: byFencer<T, E, F>(filtered),
  };
}
